import $ from 'jquery'
import _ from 'lodash'
import { isInitialQuestion } from './faq-initial-sections'
import { startupProfileFieldLabel } from 'modules/startup-profile/filters'

export const PROFILE_PROGRESS_THRESHOLD = 80

function getProfileProgressFields(startup) {
  return [
    { name: 'name' },
    { name: 'logo_url' },
    {
      name: 'sectors',
      validator(startup) {
        return (startup.sectors || []).length > 0
      },
    },
    { name: 'caption' },
    { name: 'country' },
    { name: 'city' },
    {
      name: 'business_type',
      validator(startup) {
        return (startup.business_type || []).length > 0
      },
    },
    {
      name: 'product_type',
      validator(startup) {
        return (startup.product_type || []).length > 0
      },
    },
    { name: 'stage' },
    {
      name: 'annual_revenue',
      validator(startup) {
        return startup.annual_revenue >= 0
      },
    },
    {
      name: 'monthly_recurring_revenue',
      validator(startup) {
        return startup.monthly_recurring_revenue >= 0
      },
    },
    {
      name: 'current_round',
      label: 'Current round',
      help: 'Provide type and size of the current round',
      validator(startup) {
        const roundSizeIsDefiened =
          _.isNumber(startup.round_size) && startup.round_size > 0
        const roundTypeIsDefiened = startup.round_type !== null
        return roundSizeIsDefiened && roundTypeIsDefiened
      },
    },
    {
      name: 'team',
      label: 'Team',
      help: 'Provide at least one team member',
      validator(startup) {
        return (startup.members || []).length >= 1
      },
    },
    {
      name: 'documents',
      label: 'Documents',
      help: 'Provide at least one pitch deck',
      validator(startup) {
        const pitchDocuments = (startup.documents || []).filter(
          (d) => d.is_pitch,
        )
        return pitchDocuments.length >= 1
      },
    },
  ].concat(getFaqQuestionsFields({ startup }))
}

function getItemsWithInitialQuestion({ startup }) {
  const items = []
  startup.faq.forEach((obj) => {
    obj.items.forEach((item) => {
      if (
        isInitialQuestion({ sectionName: obj.section, question: item.question })
      ) {
        items.push(item)
      }
    })
  })
  return items
}

function getFaqQuestionsFields({ startup }) {
  const itemsWithInitialQuestion = getItemsWithInitialQuestion({ startup })
  const faqQuestions = itemsWithInitialQuestion.map((item, idx) => {
    return {
      name: `faq_question_${idx}`,
      label: item.question,
      validator(startup) {
        const parsedAnswer = $.parseHTML(item.answer || '')
        const answer = $.text(parsedAnswer)
        return !!answer
      },
    }
  })

  return [
    {
      name: 'faq_header',
      label: 'QA',
      validator(startup) {
        const allItems = getAllFaqItems(startup.faq)
        const answeredItems = getAnsweredFaqItems(allItems)
        return (
          answeredItems.length > 0 && answeredItems.length === allItems.length
        )
      },
    },
  ].concat(faqQuestions)
}

function getAllFaqItems(sections) {
  return _.flatMap(sections.map((section) => section.items))
}

function getAnsweredFaqItems(allItems) {
  return allItems.filter((item) => {
    try {
      return !!$(item.answer).text()
    } catch (e) {
      return !!item.answer
    }
  })
}

export function getReport(startup, threshold = PROFILE_PROGRESS_THRESHOLD) {
  const fields = getFields(startup)
  const progress = getProgress(fields)
  const progressStatus = progress === 100 ? 'success' : null

  return {
    fields,
    progress,
    progressStatus,
    threshold,
    isReady: progress >= threshold,
  }
}

function getProgress(fields) {
  const finishedFields = fields.filter((field) => field.done)
  const finished = _.sum(finishedFields.map((field) => field.weight))
  const total = _.sum(fields.map((field) => field.weight))
  return asProgressNumber({ finished, total })
}

function asProgressNumber({ finished, total }) {
  const progress = ((finished * 100) / total).toFixed(2)
  return Number(progress)
}

function getFilledFields(startup) {
  return getFields(startup).filter((field) => field.done)
}

function getFields(startup) {
  const profileProgressFields = getProfileProgressFields(startup)
  return profileProgressFields.map((field) => {
    const value = startup[field.name]

    // by default, weight of each field is 1. But allow to override this
    let weight = 1
    if (field.weight) {
      weight = field.weight(startup)
    }

    let done
    if (field.validator) {
      done = field.validator(startup)
    } else {
      done = !!value
    }

    let innerProgress = done ? 1 : 0
    if (field.innerProgress) {
      innerProgress = field.innerProgress(startup)
    }

    return {
      name: field.name,
      help: field.help,
      label: field.label || startupProfileFieldLabel(field.name),
      value: startup[field.name],
      done,
      weight,
      innerProgress,
      section: getSection(field.name),
    }
  })
}

export const SECTIONS = {
  OVERVIEW: 'OVERVIEW',
  DOCUMENTS: 'DOCUMENTS',
  TEAM: 'TEAM',
  FAQ: 'FAQ',
}

function getSection(fieldName) {
  if (fieldName === 'documents') {
    return SECTIONS.DOCUMENTS
  } else if (fieldName === 'team') {
    return SECTIONS.TEAM
  } else if (/^faq_/.test(fieldName)) {
    return SECTIONS.FAQ
  } else {
    return SECTIONS.OVERVIEW
  }
}
